import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Breadcrumbs, Button, Card, Chip, Link, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useGetConnectorHandler } from '../dataHandlers/getConnectorHandler';
import { useGetVersionHandler } from '../dataHandlers/getVersionHandler';
import { useFieldCatalogHandler } from '../dataHandlers/getSchemaHandler';
import MappingDetailViewer from '../components/MappingDetailViewer';
import { parseConnectorVersionParam } from '../utils/connectorRouteUtils';

const VersionDetailPage = () => {
  const { connectorKey, versionNumber } = useParams();
  const navigate = useNavigate();
  const decodedKey = decodeURIComponent(connectorKey);
  const versionNum = parseConnectorVersionParam(versionNumber);

  const { connector } = useGetConnectorHandler(decodedKey);
  const { version, isLoading } = useGetVersionHandler(decodedKey, versionNum);
  const { catalog: fieldCatalog, isLoading: isCatalogLoading } = useFieldCatalogHandler(
    connector?.document_type,
    version?.marmin_api_version,
  );

  useDocumentTitle(`v${versionNum} details — ${connector?.name || decodedKey}`);

  const isEditable = version?.status === 'DRAFT' || version?.status === 'TESTING';
  const builderPath = `/connectors/${encodeURIComponent(decodedKey)}/versions/${versionNum}/builder`;

  const pageScrollSx = {
    height: '86vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    p: 1,
    pb: 4,
    color: '#fff',
    ml: { xs: 2, md: 6 },
    mr: { xs: 2, md: 6 },
  };

  if (isLoading || isCatalogLoading || !version) {
    return (
      <Box sx={{ ...pageScrollSx, textAlign: 'center' }}>
        <Typography sx={{ pt: 4 }}>Loading version…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={pageScrollSx}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component="button" underline="hover" color="inherit" onClick={() => navigate('/connectors')}>
          Connectors
        </Link>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate(`/connectors/${encodeURIComponent(decodedKey)}`)}
        >
          {decodedKey}
        </Link>
        <Typography color="text.primary">v{versionNum}</Typography>
      </Breadcrumbs>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/connectors/${encodeURIComponent(decodedKey)}`)}>
          Back to versions
        </Button>
        {isEditable && (
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(builderPath)}>
            Edit mapping
          </Button>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={600}>
          Mapping details — v{versionNum}
        </Typography>
        <Chip
          label={version.status}
          size="small"
          color={version.status === 'ACTIVE' ? 'success' : version.status === 'DRAFT' ? 'default' : 'warning'}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {connector?.document_type} · API {version.marmin_api_version}
      </Typography>

      <Card sx={{ p: 3, backgroundColor: 'background.paper' }}>
        <MappingDetailViewer
          rawConfig={version.raw_config}
          compiledConfig={version.compiled_config}
          fieldCatalog={fieldCatalog}
        />
      </Card>
    </Box>
  );
};

export default VersionDetailPage;
